/**
 * ChatMessageBubble — einzelne Nachricht im Thread
 *
 * Layout-Logik (Messenger-Standard):
 *   - Eigene Nachrichten:       rechts ausgerichtet, glass-surface-soft
 *   - Chat-Partner-Nachrichten: links ausgerichtet, glass-surface-soft mit Gold-Ring
 *   - System-Nachrichten:       zentriert, ohne Bubble
 *
 * "Eigene" ist abhängig vom Viewer:
 *   - viewerType='user'  → User sieht eigene User-Nachrichten rechts, Admin links
 *   - viewerType='admin' → Admin sieht eigene Admin-Nachrichten rechts, User links
 *
 * Default: 'user' (für rückwärtskompatibles Mounten in der User-Bubble).
 *
 * Status-Anzeige (Optimistic UI, 3-Stufen-Häkchen wie WhatsApp):
 *   - sending   → Uhren-Icon + opacity 60%
 *   - sent      → ✓ einfach grau           (vom Server bestätigt)
 *   - delivered → ✓✓ doppelt grau          (Empfänger-Browser hat Frame empfangen)
 *   - read      → ✓✓ doppelt brand-gold + "gelesen HH:MM" (Empfänger hat Chat geöffnet)
 *   - failed    → ✕ rot
 *
 * Status-Icons werden NUR bei eigenen Nachrichten angezeigt — die Status-
 * Information (gelesen / nicht gelesen) interessiert den Sender, nicht
 * den Empfänger.
 */

import React, { useRef, useState } from 'react';
import { CornerUpLeft, Image as ImageIcon } from 'lucide-react';
import type { UIMessage } from '../../hooks/useChatConversation';
import { getImageUrl } from '../../utils/imageUtils';
import { ImageLightboxTW } from '../blog/ImageLightboxTW';

interface ChatMessageBubbleProps {
  message: UIMessage;
  /** Aus wessen Perspektive wird gerendert? Default: 'user' */
  viewerType?: 'user' | 'admin';
  /**
   * Wird aufgerufen, wenn der User eine Antwort auf diese Nachricht starten will.
   * Trigger:
   *   - Eigene Bubble (rechts):     Wischen NACH LINKS um >= 60 px
   *   - Partner-Bubble (links):     Wischen NACH RECHTS um >= 60 px
   * Mauseingabe wird auch unterstützt (Klicken+Ziehen).
   */
  onReply?: () => void;
  /**
   * Wird aufgerufen, wenn der User auf die Quote-Box tippt (springe zum Original).
   * Wenn nicht gesetzt, ist die Quote-Box nicht klickbar.
   */
  onJumpToMessage?: (messageId: string) => void;
  /**
   * Visueller Hinweis-Flash auf der Bubble: Brand-Ring fadet nach 1.5s aus.
   * Wird vom Container gesetzt, wenn der User von einem Quote-Tap hier hin
   * gescrollt wurde.
   */
  isHighlighted?: boolean;
}

// Ab dieser Schwelle gilt die Geste als "ausgelöst" → onReply()
const SWIPE_THRESHOLD_PX = 60;
// Maximale visuelle Auslenkung (Bubble bewegt sich nur leicht, das Reply-Icon
// erscheint dahinter)
const SWIPE_MAX_PX = 80;
// Wenn der vertikale Anteil größer als der horizontale ist, abbrechen (User
// scrollt vertikal — nicht stören).
const SWIPE_CANCEL_RATIO = 1.0;

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(d);
  } catch {
    return '';
  }
}

function StatusIcon({ status }: { status: UIMessage['status'] }): React.ReactElement | null {
  switch (status) {
    case 'sending':
      return (
        <svg className="w-3 h-3 inline-block opacity-50" viewBox="0 0 16 16" fill="none" aria-label="wird gesendet">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'sent':
      return (
        <svg className="w-3 h-3 inline-block opacity-60" viewBox="0 0 16 16" fill="none" aria-label="gesendet">
          <path d="M3 8.5L6.5 12L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'delivered':
      return (
        <svg className="w-4 h-3 inline-block opacity-60" viewBox="0 0 20 16" fill="none" aria-label="zugestellt">
          <path d="M2 8.5L5.5 12L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 8.5L11.5 12L18 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'read':
      return (
        <svg className="w-4 h-3 inline-block text-brand" viewBox="0 0 20 16" fill="none" aria-label="gelesen">
          <path d="M2 8.5L5.5 12L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 8.5L11.5 12L18 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'failed':
      return <span className="text-red-400 text-[11px]" aria-label="fehlgeschlagen">✕</span>;
    default:
      return null;
  }
}

export function ChatMessageBubble({
  message,
  viewerType = 'user',
  onReply,
  onJumpToMessage,
  isHighlighted
}: ChatMessageBubbleProps): React.ReactElement {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Swipe-Geste-State — translateX wird live während des Drags gesetzt.
  // Nur als State (nicht Ref) damit React rerendert und das transform sichtbar
  // wird. Das Reply-Indikator-Icon hinter der Bubble fadet anhand des Werts ein.
  const [dragX, setDragX] = useState(0);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    pointerId: number;
    /** Ab wann ist klar, dass es ein horizontaler Swipe ist (nicht Scroll)? */
    locked: boolean;
  } | null>(null);

  const isSystem = message.senderType === 'system';

  // System-Nachrichten zentriert, ohne Bubble
  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <p className="text-[10px] opacity-40 italic">{message.content}</p>
      </div>
    );
  }

  // "Eigene Nachricht" aus Viewer-Perspektive
  const isMine = message.senderType === viewerType;
  const hasAttachments = message.attachments.length > 0;
  const hasText = message.content.trim().length > 0;

  // Swipe-Richtung je nach Bubble-Seite:
  //   isMine=true  (rechts) → Wischen nach LINKS  (delta < 0)
  //   isMine=false (links)  → Wischen nach RECHTS (delta > 0)
  // Wir rejecten Bewegungen in die "falsche" Richtung sofort.
  const allowedDirection: 1 | -1 = isMine ? -1 : 1;

  const swipeEnabled = onReply !== undefined && message.status !== 'sending';

  // Lightbox-Set: nur die Bilder DIESER Nachricht (Galerie pro Nachricht-Bündel,
  // nicht über die ganze Konversation hinweg). Caption wird mit übergeben —
  // Lightbox zeigt sie als Bild-Untertitel an.
  const lightboxImages = message.attachments.map((a) => ({
    src: getImageUrl(a.storagePath),
    alt: a.caption || a.fileName,
    ...(a.caption ? { caption: a.caption } : {})
  }));

  // ============================
  // Swipe-Handler (Pointer Events)
  // ============================
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!swipeEnabled) return;
    // Nur primäre Maustaste / Touch / Pen
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      locked: false
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;

    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Vor dem "Lock" entscheiden wir, ob das ein Swipe ist oder ein Scroll:
    // Sobald der User > 6 px bewegt hat, schauen wir ob horizontal oder vertikal.
    if (!s.locked) {
      if (absDx < 6 && absDy < 6) return;
      // Vertikal überwiegt → User scrollt → Geste verwerfen
      if (absDy > absDx * SWIPE_CANCEL_RATIO) {
        dragStateRef.current = null;
        setDragX(0);
        return;
      }
      // Falsche Richtung → verwerfen (kein "Snap-back" nötig, war nie locked)
      if (Math.sign(dx) !== allowedDirection) {
        dragStateRef.current = null;
        setDragX(0);
        return;
      }
      s.locked = true;
      // Pointer fangen, damit weitere Events sicher bei uns landen, auch wenn
      // der User aus dem Bubble-Bereich rauszieht.
      try {
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      } catch { /* ignore — manche Browser meckern bei bereits-captured */ }
    }

    // In erlaubter Richtung clampen, in falscher Richtung auf 0 halten
    let nextDragX = dx;
    if (allowedDirection === -1) {
      nextDragX = Math.min(0, Math.max(-SWIPE_MAX_PX, nextDragX));
    } else {
      nextDragX = Math.max(0, Math.min(SWIPE_MAX_PX, nextDragX));
    }
    setDragX(nextDragX);
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const wasLocked = s.locked;
    const finalDx = dragX;
    dragStateRef.current = null;
    setDragX(0);
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch { /* ignore */ }

    if (!wasLocked) return;
    if (Math.abs(finalDx) >= SWIPE_THRESHOLD_PX && onReply) {
      // Haptisches Feedback (nur Android — iOS ignoriert es)
      try { navigator.vibrate?.(20); } catch { /* ignore */ }
      onReply();
    }
  };

  // Reply-Indikator-Opazität je nach Swipe-Fortschritt: fadet linear ein
  const replyIconOpacity = Math.min(1, Math.abs(dragX) / SWIPE_THRESHOLD_PX);

  // Echte Server-ID (nicht temp-...) für Scroll-Anker. Optimistic-Bubbles
  // bekommen keinen Anker, weil ihre temp-ID nicht referenzierbar ist.
  const isPersistedId = !message.id.startsWith('temp-') && !message.id.startsWith('admin-temp-');

  // Rechtsklick: triggert direkt onReply (Desktop-Komfort, identisch zur Wisch-Geste).
  // preventDefault unterdrückt das native Browser-Kontextmenü.
  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!swipeEnabled || !onReply) return;
    e.preventDefault();
    onReply();
  };

  return (
    <>
      <div
        className={`flex ${isMine ? 'justify-end' : 'justify-start'} relative group/bubble items-center`}
        {...(isPersistedId ? { 'data-message-id': message.id } : {})}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onContextMenu={handleContextMenu}
      >
        {/* Reply-Icon-Indikator: hinter der Bubble auf der Seite, in die der
            User wischt. Nur bei swipeEnabled gerendert. */}
        {swipeEnabled && Math.abs(dragX) > 4 && (
          <div
            className={`
              absolute top-1/2 -translate-y-1/2
              ${isMine ? 'right-2' : 'left-2'}
              w-8 h-8 rounded-full
              bg-brand/20 ring-1 ring-inset ring-brand/40
              flex items-center justify-center
              pointer-events-none
            `}
            style={{ opacity: replyIconOpacity }}
            aria-hidden="true"
          >
            <CornerUpLeft className={`w-4 h-4 text-brand ${isMine ? '' : 'scale-x-[-1]'}`} strokeWidth={2.5} />
          </div>
        )}

        {/* Hover-Reply-Button (Desktop-Komfort): erscheint NEBEN der Bubble
            wenn der User mit der Maus drüber fährt. Auf eigenen Bubbles links
            der Bubble, auf Partner-Bubbles rechts. Bei Touch-Geräten via
            sm: zwar sichtbar, aber dort wird typischerweise gewischt. */}
        {swipeEnabled && (
          <button
            type="button"
            aria-label="Auf diese Nachricht antworten"
            title="Antworten"
            onClick={(e) => {
              e.stopPropagation();
              onReply?.();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`
              ${isMine ? 'order-first mr-1.5' : 'order-last ml-1.5'}
              w-7 h-7 rounded-full shrink-0
              bg-white/70 dark:bg-gray-800/70
              ring-1 ring-inset ring-gray-300/70 dark:ring-white/10
              backdrop-blur-md
              text-gray-700 dark:text-gray-200
              hidden sm:flex items-center justify-center
              opacity-0 group-hover/bubble:opacity-100
              focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand/40
              hover:bg-brand/15 hover:text-brand hover:ring-brand/40
              transition-opacity duration-150
            `}
          >
            <CornerUpLeft className={`w-3.5 h-3.5 ${isMine ? '' : 'scale-x-[-1]'}`} strokeWidth={2.5} />
          </button>
        )}

        <div
          className={`
            max-w-[85%] sm:max-w-[80%]
            text-gray-900 dark:text-white
            backdrop-blur-lg backdrop-saturate-200
            shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.06)]
            dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.3),0_1px_2px_-1px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.12)]
            bg-white/85 dark:bg-gray-800/50 ring-1 ring-inset ring-gray-300/70 dark:ring-white/10
            ${hasAttachments && !hasText ? 'rounded-xl p-0 overflow-hidden' : 'rounded-2xl'}
            ${hasAttachments && hasText ? 'p-1.5' : ''}
            ${!hasAttachments ? 'px-3 py-2' : ''}
            ${isMine ? 'rounded-tr-sm' : 'rounded-tl-sm dark:ring-brand/30'}
            ${message.status === 'sending' ? 'opacity-60' : ''}
            ${message.status === 'failed' ? '!ring-red-400/60 dark:!ring-red-400/40' : ''}
            ${dragX === 0 ? 'transition-transform duration-200' : ''}
            ${swipeEnabled ? 'touch-pan-y' : ''}
            ${isHighlighted
              ? '!ring-2 !ring-brand shadow-[0_0_0_4px_rgba(241,196,15,0.25)] transition-shadow duration-700'
              : 'transition-shadow duration-700'}
          `}
          style={{ transform: `translateX(${dragX}px)` }}
        >
          {/* Quote-Box: zitiert die Nachricht, auf die geantwortet wird.
              Sitzt OBEN in der Bubble. Tap → springt zur Original-Nachricht. */}
          {message.replyTo && (() => {
            const replyTargetId = message.replyTo.id;
            const isJumpable = onJumpToMessage !== undefined;
            const wrapperClasses = `
              ${hasAttachments && !hasText ? 'mx-1.5 mt-1.5' : ''}
              ${hasAttachments && hasText ? 'mb-1' : ''}
              ${!hasAttachments ? '-mx-1 mb-1.5' : ''}
              block w-full text-left
              px-2 py-1
              rounded-md
              border-l-2 border-brand
              bg-black/5 dark:bg-white/5
              ${isJumpable ? 'hover:bg-black/10 dark:hover:bg-white/10 cursor-pointer transition-colors duration-150' : ''}
            `;
            const inner = (
              <>
                <p className="text-[10px] font-semibold text-brand leading-tight">
                  {message.replyTo.senderType === viewerType ? 'Du' : 'Support'}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  {message.replyTo.hasAttachments && message.replyTo.contentPreview.length === 0 && (
                    <ImageIcon className="w-2.5 h-2.5 opacity-60 shrink-0" strokeWidth={2} />
                  )}
                  <p className="text-[11px] opacity-70 leading-snug truncate">
                    {message.replyTo.contentPreview.length > 0
                      ? message.replyTo.contentPreview
                      : message.replyTo.hasAttachments
                        ? 'Bild'
                        : 'Nachricht'}
                  </p>
                </div>
              </>
            );
            return isJumpable ? (
              <button
                type="button"
                className={wrapperClasses}
                onClick={(e) => {
                  // Nicht innerhalb der Bubble-Geste-Handler propagieren —
                  // sonst startet PointerDown der Bubble und der Click wird
                  // vom Browser eventuell verschluckt.
                  e.stopPropagation();
                  onJumpToMessage(replyTargetId);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="Springe zur zitierten Nachricht"
              >
                {inner}
              </button>
            ) : (
              <div className={wrapperClasses}>{inner}</div>
            );
          })()}

          {/* Bild-Galerie cleaner: max-180px, dezenter Eckenradius, bei reiner
              Bild-Nachricht ohne Padding (Polaroid-Look), bei Mix mit Text gibt's
              etwas Innenrand. */}
          {hasAttachments && (
            <AttachmentGallery
              attachments={message.attachments}
              onOpen={(i) => setLightboxIdx(i)}
              embedded={hasText}
            />
          )}

          {hasText && (
            <p
              className={`
                text-[13px] leading-relaxed whitespace-pre-wrap break-words
                ${hasAttachments ? 'px-1.5 pt-2' : ''}
              `}
            >
              {message.content}
            </p>
          )}

          <div
            className={`
              flex items-center gap-1.5
              ${hasAttachments && hasText ? 'px-1.5 pb-0.5 pt-1' : ''}
              ${hasAttachments && !hasText ? 'px-2 pb-1.5 pt-1' : ''}
              ${!hasAttachments ? 'mt-1' : ''}
              ${isMine ? 'justify-end' : 'justify-start'}
            `}
          >
            <span className="text-[10px] opacity-40 leading-none">{formatTime(message.createdAt)}</span>
            {isMine && message.status === 'read' && message.readAt && (
              <span className="text-[10px] text-brand/80 leading-none">
                gelesen {formatTime(message.readAt)}
              </span>
            )}
            {isMine && <StatusIcon status={message.status} />}
          </div>
        </div>
      </div>

      {/* Lightbox: per-Nachricht-Galerie. Nur rendern wenn aktiv. */}
      {lightboxIdx !== null && (
        <ImageLightboxTW
          isOpen={true}
          imageUrl={lightboxImages[lightboxIdx]?.src ?? ''}
          {...(lightboxImages[lightboxIdx]?.caption ? { caption: lightboxImages[lightboxIdx]!.caption } : {})}
          allImages={lightboxImages.length > 1 ? lightboxImages : undefined}
          currentIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}

/**
 * Bild-Galerie innerhalb einer Bubble.
 * Layout je nach Anzahl:
 *   1 Bild  → groß, ratio aus width/height
 *   2 Bilder → side-by-side (1:1)
 *   3 Bilder → erstes Bild links groß, rechts vertikal die anderen zwei
 */
function AttachmentGallery({
  attachments,
  onOpen,
  embedded
}: {
  attachments: UIMessage['attachments'];
  onOpen: (index: number) => void;
  /** True wenn die Bubble auch Text enthält → minimal andere Padding-Logik */
  embedded?: boolean;
}): React.ReactElement {
  const count = attachments.length;
  // Maximalbreite cleaner und unauffälliger (vorher 280px → jetzt 200px,
  // sodass Bilder den Chat-Flow nicht dominieren).
  const maxWidth = '200px';

  if (count === 1) {
    const a = attachments[0]!;
    const aspectRatio = a.width && a.height ? a.width / a.height : 4 / 3;
    return (
      <div className="flex flex-col gap-1" style={{ maxWidth }}>
        <button
          type="button"
          onClick={() => onOpen(0)}
          className={`
            block ${embedded ? 'rounded-lg' : 'rounded-xl'}
            overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand/40
          `}
          style={{ aspectRatio: String(aspectRatio) }}
        >
          <img
            src={getImageUrl(a.storagePath)}
            alt={a.caption || a.fileName}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </button>
        {a.caption && (
          <p className="text-[11px] opacity-70 leading-snug px-1.5 pt-0.5 italic">
            {a.caption}
          </p>
        )}
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 rounded-xl overflow-hidden" style={{ maxWidth }}>
        {attachments.map((a, i) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onOpen(i)}
            className="aspect-square focus:outline-none focus:ring-2 focus:ring-brand/40"
            title={a.caption || undefined}
          >
            <img
              src={getImageUrl(a.storagePath)}
              alt={a.caption || a.fileName}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    );
  }

  // count === 3: links groß, rechts zwei vertikal
  return (
    <div
      className="grid grid-cols-2 grid-rows-2 gap-1 rounded-xl overflow-hidden"
      style={{ maxWidth, aspectRatio: '4/3' }}
    >
      <button
        type="button"
        onClick={() => onOpen(0)}
        title={attachments[0]!.caption || undefined}
        className="row-span-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        <img
          src={getImageUrl(attachments[0]!.storagePath)}
          alt={attachments[0]!.caption || attachments[0]!.fileName}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </button>
      <button
        type="button"
        onClick={() => onOpen(1)}
        title={attachments[1]!.caption || undefined}
        className="focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        <img
          src={getImageUrl(attachments[1]!.storagePath)}
          alt={attachments[1]!.caption || attachments[1]!.fileName}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </button>
      <button
        type="button"
        onClick={() => onOpen(2)}
        title={attachments[2]!.caption || undefined}
        className="focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        <img
          src={getImageUrl(attachments[2]!.storagePath)}
          alt={attachments[2]!.caption || attachments[2]!.fileName}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </button>
    </div>
  );
}
